import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
  type SVGProps,
} from 'react';
import { cn } from '../../../lib/utils';

const checkboxVariants = cva(
  [
    'group relative inline-flex size-4 shrink-0 items-center justify-center rounded border shadow-2xs',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/30 focus-visible:ring-offset-0',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
    'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-input bg-background',
        destructive: 'border-destructive bg-background',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface CheckboxProps
  extends
    ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, variant, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      data-slot="checkbox"
      className={cn(checkboxVariants({ variant }), className)}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        <CheckIcon className="size-3.5 group-data-[state=indeterminate]:hidden" />
        <MinusIcon className="hidden size-3.5 group-data-[state=indeterminate]:flex" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName ?? 'Checkbox';

function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden
      {...props}
    >
      <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MinusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden
      {...props}
    >
      <path d="M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

const checkboxCardVariants = cva('rounded-lg border p-4', {
  variants: {
    variant: {
      default: 'border-border bg-card',
      destructive: 'border-destructive bg-card',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface CheckboxCardProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof checkboxCardVariants> {}

const CheckboxCard = forwardRef<HTMLDivElement, CheckboxCardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="checkbox-card"
      className={cn(checkboxCardVariants({ variant }), className)}
      {...props}
    />
  )
);
CheckboxCard.displayName = 'CheckboxCard';

export interface CheckboxFieldProps extends Omit<CheckboxProps, 'id'> {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  orientation?: 'left' | 'right';
}

function CheckboxField({
  id,
  label,
  description,
  orientation = 'left',
  variant = 'default',
  className,
  disabled,
  ...checkboxProps
}: CheckboxFieldProps) {
  const isDestructive = variant === 'destructive';

  return (
    <div
      className={cn(
        'flex items-start gap-2',
        orientation === 'right' && 'flex-row-reverse',
        disabled && 'opacity-50',
        className
      )}
    >
      <Checkbox id={id} disabled={disabled} variant={variant} {...checkboxProps} />
      <div className="grid min-w-0 flex-1 gap-1">
        <label
          htmlFor={id}
          className={cn(
            'cursor-pointer text-sm font-medium leading-none text-foreground',
            isDestructive && 'text-destructive',
            disabled && 'cursor-not-allowed'
          )}
        >
          {label}
        </label>
        {description != null && description !== '' ? (
          <p className="text-sm leading-5 text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}
CheckboxField.displayName = 'CheckboxField';

export { Checkbox, CheckboxCard, CheckboxField, checkboxCardVariants, checkboxVariants };
